import { PencilIcon, KeyIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@nextui-org/react';
import React, { Key } from 'react';
import { toast } from 'react-toastify';

import { NoteIconUI } from '@portal/components/common/NoteIcon.ui';
import { TerminalIconUI } from '@portal/components/common/TerminalIcon.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { UnifiedNavigationUI } from '@portal/components/common/UnifiedNavigation.ui';
import { UserDropdown } from '@portal/components/UserDropdown';
import { tsrestClient } from '@portal/provider/tsrest';
import { useAuthStatus } from '@portal/utils';

import { AddUserModal } from './components/AddUserModal';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { DeleteUserModal } from './components/DeleteUserModal';
import { EditUserModal } from './components/EditUserModal';

interface User {
  id: number;
  username: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export const Users = () => {
  const { isSuperAdmin } = useAuthStatus();
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  // Form states
  const [formData, setFormData] = React.useState({
    username: '',
    password: '',
    is_active: true,
    is_admin: false,
  });
  const [passwordData, setPasswordData] = React.useState({
    password: '',
    confirmPassword: '',
  });

  // Password visibility states
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);

  // Fetch users
  const { data: usersData, refetch: refetchUsers } = tsrestClient.users.get.useQuery(['users'], {
    headers: {
      authorization: localStorage.getItem('token') || '',
    },
  });

  const users = usersData?.body?.users || [];

  // Create user mutation
  const createUserMutation = tsrestClient.users.create.useMutation({
    onSuccess: () => {
      toast('User created successfully', { type: 'success' });
      refetchUsers();
      setIsAddModalOpen(false);
      setFormData({ username: '', password: '', is_active: true, is_admin: false });
      setShowPassword(false);
    },
    onError: error => {
      toast(`Failed to create user: ${(error.body as any)?.message || 'Unknown error'}`, { type: 'error' });
    },
  });

  // Update user mutation
  const updateUserMutation = tsrestClient.users.update.useMutation({
    onSuccess: (_, variables) => {
      // Check if user was deactivated
      const wasDeactivated = variables.body?.is_active === false;

      if (wasDeactivated) {
        toast('User deactivated and sessions cleared successfully', { type: 'success' });
      } else {
        toast('User updated successfully', { type: 'success' });
      }

      refetchUsers();
      setIsEditModalOpen(false);
      setSelectedUser(null);
      setFormData({ username: '', password: '', is_active: true, is_admin: false });
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to update user: ${errorMessage}`, { type: 'error' });

      // If it's an admin user restriction, show a more specific message
      if (errorMessage.includes('admin user cannot be modified')) {
        toast('The admin user can only have their password changed', { type: 'warning' });
      }
    },
  });

  // Update password mutation
  const updatePasswordMutation = tsrestClient.users.updatePassword.useMutation({
    onSuccess: () => {
      toast('Password updated successfully', { type: 'success' });
      setIsPasswordModalOpen(false);
      setSelectedUser(null);
      setPasswordData({ password: '', confirmPassword: '' });
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to update password: ${errorMessage}`, { type: 'error' });

      // If it's an admin user restriction, show a more specific message
      if (errorMessage.includes('admin user password cannot be force updated by non-admin username')) {
        toast('Only the admin user can force update the admin password', { type: 'warning' });
      } else if (errorMessage.includes('admin access required for force password update')) {
        toast('Admin access required for force password update', { type: 'warning' });
      }
    },
  });

  // Delete user mutation
  const deleteUserMutation = tsrestClient.users.delete.useMutation({
    onSuccess: () => {
      toast('User deleted successfully', { type: 'success' });
      refetchUsers();
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to delete user: ${errorMessage}`, { type: 'error' });

      // If it's an admin user restriction, show a more specific message
      if (errorMessage.includes('admin user cannot be deleted')) {
        toast('The admin user cannot be deleted', { type: 'warning' });
      }
    },
  });

  const handleCreateUser = () => {
    if (!formData.username || !formData.password) {
      toast('Username and password are required', { type: 'error' });
      return;
    }
    createUserMutation.mutate({
      body: {
        username: formData.username,
        password: formData.password,
        is_admin: formData.is_admin,
      },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  const handleUpdateUser = () => {
    if (!selectedUser || !formData.username) {
      toast('Username is required', { type: 'error' });
      return;
    }
    updateUserMutation.mutate({
      params: { id: selectedUser.id.toString() },
      body: {
        username: formData.username,
        is_active: formData.is_active,
        is_admin: formData.is_admin,
      },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  const handleUpdatePassword = () => {
    if (!selectedUser || !passwordData.password) {
      toast('Password is required', { type: 'error' });
      return;
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      toast('Passwords do not match', { type: 'error' });
      return;
    }
    updatePasswordMutation.mutate({
      params: { id: selectedUser.id.toString() },
      body: { newPassword: passwordData.password },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  const handleDeleteUser = (userId: number) => {
    deleteUserMutation.mutate({
      params: { id: userId.toString() },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      is_active: user.is_active,
      is_admin: user.is_admin,
    });
    setIsEditModalOpen(true);
  };

  const columns = [
    { key: 'username', label: 'Username', width: 180 },
    { key: 'status', label: 'Status', width: 80 },
    { key: 'role', label: 'Role', width: 80 },
    { key: 'created', label: 'Created', width: 120 },
    { key: 'actions', label: 'Actions', width: 100 },
  ];

  const renderCell = (user: User, columnKey: Key) => {
    switch (columnKey) {
      case 'username':
        return (
          <div className='flex flex-col gap-0.5'>
            <TypographyUI className='font-semibold text-gray-900 text-sm'>{user.username}</TypographyUI>
            <TypographyUI className='text-xs text-gray-500'>ID: {user.id}</TypographyUI>
          </div>
        );
      case 'status':
        return (
          <Chip className='capitalize' color={user.is_active ? 'success' : 'danger'} size='sm' variant='flat'>
            {user.is_active ? 'Active' : 'Inactive'}
          </Chip>
        );
      case 'role':
        return (
          <Chip className='capitalize' color={user.is_admin ? 'primary' : 'default'} size='sm' variant='flat'>
            {user.is_admin ? 'Admin' : 'User'}
          </Chip>
        );
      case 'created':
        return (
          <div className='flex flex-col gap-0.5'>
            <TypographyUI className='text-xs text-gray-900'>
              {new Date(user.created_at).toLocaleDateString()}
            </TypographyUI>
            <TypographyUI className='text-xs text-gray-500'>
              {new Date(user.created_at).toLocaleTimeString()}
            </TypographyUI>
          </div>
        );
      case 'actions':
        return (
          <div className='flex gap-0.5'>
            <Button
              size='sm'
              color='primary'
              variant='light'
              isIconOnly
              onPress={() => openEditModal(user)}
              title='Edit User'
              isDisabled={user.username === 'admin'}
            >
              <PencilIcon className='h-3 w-3' />
            </Button>
            <Tooltip
              content={
                user.username === 'admin' && !isSuperAdmin
                  ? 'Only the super admin can change the super admin password'
                  : 'Change Password'
              }
              isDisabled={!(user.username === 'admin' && !isSuperAdmin)}
            >
              <Button
                size='sm'
                color='secondary'
                variant='light'
                isIconOnly
                onPress={() => {
                  setSelectedUser(user);
                  setIsPasswordModalOpen(true);
                }}
                title='Change Password'
                isDisabled={user.username === 'admin' && !isSuperAdmin}
              >
                <KeyIcon className='h-3 w-3' />
              </Button>
            </Tooltip>
            <Button
              size='sm'
              color='danger'
              variant='light'
              isIconOnly
              onPress={() => {
                setSelectedUser(user);
                setIsDeleteModalOpen(true);
              }}
              title='Delete User'
              isDisabled={user.username === 'admin'}
            >
              <TrashIcon className='h-3 w-3' />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Navigation Header */}
      <div className='bg-white border-b border-gray-200 shadow-sm mb-6'>
        <div className='py-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-6'>
              <UnifiedNavigationUI />
              <div>
                <TypographyUI className='text-lg font-semibold' color='blue-gray'>
                  User Management
                </TypographyUI>
                <TypographyUI className='text-sm' color='gray'>
                  Manage system users and permissions
                </TypographyUI>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <TerminalIconUI />
              <NoteIconUI />
              <UserDropdown />
            </div>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4 max-w-6xl mx-auto'>
        {/* Header with Add User Button and Table Title */}
        <div className='flex items-center justify-between'>
          <TypographyUI className='text-sm text-gray-600'>
            Total {users.length} user{users.length !== 1 ? 's' : ''}, with {users.filter(u => u.is_admin).length} admin
            user{users.filter(u => u.is_admin).length !== 1 ? 's' : ''}, and {users.filter(u => !u.is_admin).length}{' '}
            standard user{users.filter(u => !u.is_admin).length !== 1 ? 's' : ''}
          </TypographyUI>
          <Button color='primary' onPress={() => setIsAddModalOpen(true)}>
            Add User
          </Button>
        </div>

        {/* Users Table */}
        <Table aria-label='users list' removeWrapper isStriped isHeaderSticky>
          <TableHeader columns={columns}>
            {column => (
              <TableColumn key={column.key} align='start' width={column.width}>
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={users} emptyContent='No users found'>
            {user => (
              <TableRow key={user.id}>{columnKey => <TableCell>{renderCell(user, columnKey)}</TableCell>}</TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onOpenChange={() => setIsAddModalOpen(false)}
        formData={formData}
        onFormDataChange={setFormData}
        showPassword={showPassword}
        onShowPasswordChange={setShowPassword}
        onSubmit={handleCreateUser}
        isLoading={createUserMutation.isLoading}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={isEditModalOpen}
        onOpenChange={() => setIsEditModalOpen(false)}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdateUser}
        isLoading={updateUserMutation.isLoading}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onOpenChange={() => setIsPasswordModalOpen(false)}
        passwordData={passwordData}
        onPasswordDataChange={setPasswordData}
        showNewPassword={showNewPassword}
        showConfirmPassword={showConfirmPassword}
        onShowNewPasswordChange={setShowNewPassword}
        onShowConfirmPasswordChange={setShowConfirmPassword}
        onSubmit={handleUpdatePassword}
        isLoading={updatePasswordMutation.isLoading}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onOpenChange={() => setIsDeleteModalOpen(false)}
        selectedUser={selectedUser}
        onSubmit={() => selectedUser && handleDeleteUser(selectedUser.id)}
        isLoading={deleteUserMutation.isLoading}
      />
    </>
  );
};
